import {
	ActorRefFrom,
	assign,
	createMachine,
	EventObject,
	Interpreter,
	InterpreterFrom,
	Prop,
	State,
	StateFrom,
	StateMachine,
	interpret,
} from 'xstate'

import routes from '@Constants/Routes'
import { createContext, useContext, useEffect, useRef } from 'react'
import { useActor, useInterpret, useMachine } from '@xstate/react'
import { useLocation, useNavigate } from 'react-router'
import { useAppSelector } from '@Hooks/ReduxHooks'
import { useSearchParams } from 'react-router-dom'
import { t } from 'i18next'

export type RedirectCode = 101 | 102 | 103 | 104 | 105
export const excludedRedirections = [104]
type Redirections = {
	[key in RedirectCode]?: string
}

export const redirections: Redirections = {
	101: `${routes.signup}/1`,
	103: `${routes.signup}/2`,
	104: `${routes.signup}/3`,
	// 105 = device verification. Without an entry the lookup returned undefined,
	// so the machine "navigated" to wherever the student already was and could
	// never bring an unverified browser back to the challenge — while the header
	// still treated the unresolved 105 as blocking. Login navigated there
	// explicitly, so the first hop worked and nothing after it did.
	105: routes.verifyDevice,
}

type MachineType = { nextRoute: string; pathname: string }
type MachineEventType = { type: string; redirect?: number; pathname?: string }
const checkRedirections = assign<MachineType, MachineEventType>({
	nextRoute: (context, event) => {
		if (event.redirect !== undefined && !excludedRedirections.includes(event.redirect)) {
			const next = redirections[event.redirect as RedirectCode]
			return next ?? event.pathname ?? '/'
		}

		return event.pathname ?? '/'
	},
})
const routingMachine = createMachine(
	{
		id: 'navigation',
		initial: '/',
		on: {
			navigate: {
				target: 'CheckRedirections',
				actions: ['checkRedirections'],
			},
		},
		context: {
			nextRoute: '',
			pathname: '',
		},
		states: {
			'/': {
				on: {
					navigate: {
						target: 'CheckRedirections',
						actions: ['checkRedirections'],
					},
				},
			},
			CheckRedirections: {},
		},
	},
	{
		actions: { checkRedirections },
	}
)

type IRoutingContext = { state: StateFrom<typeof routingMachine>; send: Prop<InterpreterFrom<typeof routingMachine>, 'send'> }
const RoutingContext = createContext<IRoutingContext | null>(null)

export const RoutingProvider: React.FC = ({ children }) => {
	const [state, send, service] = useMachine(routingMachine)

	const navigate = useNavigate()
	const [params] = useSearchParams()
	const { pathname } = useLocation()
	const { isAuth, redirect } = useAppSelector(state => state.auth)
	useEffect(() => {
		service.start(routingMachine.initialState)
		service.onChange(({ nextRoute, pathname }) => {
			navigate({ pathname: nextRoute, search: params.toString() })
		})
	}, [])

	useEffect(() => {
		// Excluded codes are filtered here, not just inside the machine.
		//
		// `checkRedirections` already refuses to route them, but it still assigns
		// `nextRoute = event.pathname` — a fresh context object, so `onChange`
		// fires and navigates to the page we are already on, pushing a duplicate
		// history entry on every route change. That was dormant while `redirect`
		// was never restored on boot; now that /auth/me reports 104 again, it
		// would make the back button need two presses everywhere.
		if (redirect && excludedRedirections.includes(redirect)) return
		if (redirect && redirections[redirect as RedirectCode] !== pathname) service.send('navigate', { redirect, pathname })
	}, [redirect, pathname, service])

	return <RoutingContext.Provider value={{ state, send }}>{children}</RoutingContext.Provider>
}

export const useRoutingContext = () => {
	const context = useContext(RoutingContext)
	if (!context) {
		throw new Error('No RoutingProvider found !!!')
	}
	return { ...context }
}
